import React, { useEffect, useState, useCallback } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled from 'styled-components';
import { FaLandmark, FaSyncAlt, FaPlus, FaTrashAlt, FaExclamationTriangle, FaExternalLinkAlt } from 'react-icons/fa';
import {
  TechShell, TechPageHeader, TechSection, TechInput, TechSelect, TechButton, TechButtonGhost,
  StatStrip, StatTile, StatLabel, StatValue, StatSub, StatusPill, TechChip, Empty, Loading, ErrorBox,
  borderSoft, mono,
} from '../../styles/techComponents';
import { listLabor, createLabor, updateLabor, deleteLabor } from '../../services/laborService';

const TIPOS = ['ordenanza', 'pedido_informes', 'resolucion', 'comunicacion', 'proyecto'];
const ESTADOS = ['presentado', 'en_comision', 'aprobado', 'rechazado', 'archivado'];
const ESTADO_COLOR = { presentado: '#00b4d8', en_comision: '#8b5cf6', aprobado: '#22c55e', rechazado: '#ef4444', archivado: '#64748b' };

const Row = styled.div` display:grid;grid-template-columns:2fr 1fr 1fr auto auto;gap:0.5rem;align-items:center;
  padding:0.55rem 0;border-bottom:1px solid ${borderSoft};font-size:0.86rem;
  @media(max-width:800px){grid-template-columns:1fr} `;
const Form = styled.div` display:grid;grid-template-columns:2fr 1fr 1fr auto;gap:0.5rem;align-items:end;
  @media(max-width:800px){grid-template-columns:1fr 1fr} `;
const Lbl = styled.label` font-family:${mono};font-size:0.64rem;letter-spacing:0.1em;text-transform:uppercase;color:var(--text-secondary);display:block;margin-bottom:0.2rem; `;

export default function Labor() {
  const [items, setItems] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ titulo: '', tipo: 'ordenanza', estado: 'presentado' });
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const d = await listLabor(); setItems(d.items || []); setMetrics(d.metrics || {}); }
    catch (e) { setError(friendlyError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(null), 2500); };

  async function onAdd() {
    if (!form.titulo.trim()) return flash('Poné un título');
    try { const it = await createLabor(form); setItems((p) => [it, ...p]); setForm({ titulo: '', tipo: 'ordenanza', estado: 'presentado' }); flash('Proyecto cargado'); load(); }
    catch (e) { flash(friendlyError(e, 'No se pudo cargar el proyecto.')); }
  }
  async function onEstado(it, estado) {
    try { const u = await updateLabor(it.id, { estado }); setItems((p) => p.map((x) => x.id === it.id ? u : x)); load(); }
    catch (e) { flash(friendlyError(e, 'No se pudo actualizar el estado.')); }
  }
  async function onDel(it) {
    try { await deleteLabor(it.id); setItems((p) => p.filter((x) => x.id !== it.id)); load(); }
    catch (e) { flash(friendlyError(e, 'No se pudo eliminar.')); }
  }

  if (loading) return <TechShell><Loading>Cargando labor legislativa…</Loading></TechShell>;
  if (error) return <TechShell><ErrorBox><FaExclamationTriangle /> {error}</ErrorBox></TechShell>;

  return (
    <TechShell>
      <TechPageHeader
        title="Mi labor en el HCD"
        dossier="LEGISLATIVO"
        subtitle="Los proyectos propios de Chouhy: presentados, en comisión y aprobados. Productividad medible que alimenta el tablero y da contenido de gestión."
        actions={<TechButtonGhost onClick={load}><FaSyncAlt /> Actualizar</TechButtonGhost>}
      />
      <StatStrip>
        <StatTile><StatLabel>Proyectos</StatLabel><StatValue>{metrics.total || 0}</StatValue><StatSub>{metrics.ultimos_30d || 0} en 30 días</StatSub></StatTile>
        <StatTile><StatLabel>Aprobados</StatLabel><StatValue style={{ color: '#22c55e' }}>{metrics.aprobados || 0}</StatValue><StatSub>{metrics.aprobados_90d || 0} en 90 días</StatSub></StatTile>
        <StatTile><StatLabel>En trámite</StatLabel><StatValue>{metrics.en_tramite || 0}</StatValue><StatSub>presentados + en comisión</StatSub></StatTile>
        <StatTile><StatLabel>Tasa de aprobación</StatLabel><StatValue>{metrics.tasa_aprobacion || 0}%</StatValue><StatSub>del total presentado</StatSub></StatTile>
      </StatStrip>
      {msg && <StatusPill style={{ marginBottom: '0.5rem' }}>{msg}</StatusPill>}

      <TechSection title="Cargar proyecto" icon={<FaPlus />}>
        <Form>
          <div><Lbl>Título</Lbl><TechInput placeholder="Ej: Ordenanza de protección de humedales" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} /></div>
          <div><Lbl>Tipo</Lbl><TechSelect value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>{TIPOS.map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}</TechSelect></div>
          <div><Lbl>Estado</Lbl><TechSelect value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })}>{ESTADOS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}</TechSelect></div>
          <TechButton onClick={onAdd}><FaPlus /> Cargar</TechButton>
        </Form>
      </TechSection>

      <TechSection title={`Proyectos (${items.length})`} icon={<FaLandmark />}>
        {items.length === 0 && <Empty>Sin proyectos cargados. Empezá a registrar la labor legislativa de Chouhy.</Empty>}
        {items.map((it) => (
          <Row key={it.id}>
            <div>
              <div>{it.titulo}</div>
              <div style={{ fontFamily: mono, fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{it.tipo.replace('_', ' ')}{it.expediente ? ` · exp ${it.expediente}` : ''}{it.tema ? ` · ${it.tema}` : ''}</div>
            </div>
            <TechChip style={{ color: ESTADO_COLOR[it.estado], borderColor: ESTADO_COLOR[it.estado] }}>{it.estado.replace('_', ' ')}</TechChip>
            <TechSelect value={it.estado} onChange={(e) => onEstado(it, e.target.value)}>{ESTADOS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}</TechSelect>
            {it.link ? <TechButtonGhost as="a" href={it.link} target="_blank" rel="noreferrer"><FaExternalLinkAlt /></TechButtonGhost> : <span />}
            <TechButtonGhost onClick={() => onDel(it)}><FaTrashAlt /></TechButtonGhost>
          </Row>
        ))}
      </TechSection>
    </TechShell>
  );
}
